import React from 'react';
import { InstagramIcon } from '../../../components/icons/CustomIcons';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useSubscription } from '../../subscription/hooks/useSubscription';
import { useInstagramConnection } from './hooks/useInstagramConnection';
import { getInstagramDisplayHandle } from '../../../services/modules/instagramRepository';
import type { InstagramAccountFromApi } from '../../../services/modules/instagramService';

interface AccountPickerModalProps {
    accounts: InstagramAccountFromApi[];
    isSaving: boolean;
    onSelect: (account: InstagramAccountFromApi) => void;
    onClose: () => void;
}

const AccountPickerModal: React.FC<AccountPickerModalProps> = ({
    accounts,
    isSaving,
    onSelect,
    onClose,
}) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-md rounded-xl bg-surface p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-default mb-2">Selecione a conta Instagram</h3>
            <p className="text-sm text-muted mb-4">
                Sua conta Instagram Business será conectada automaticamente após o login.
            </p>
            <ul className="space-y-2 max-h-64 overflow-y-auto">
                {accounts.map((acc) => (
                    <li key={acc.instagramBusinessAccountId}>
                        <button
                            type="button"
                            disabled={isSaving}
                            onClick={() => onSelect(acc)}
                            className="w-full rounded-lg border border-border p-3 text-left hover:bg-surface-subtle transition-colors disabled:opacity-50"
                        >
                            <span className="font-medium text-default">
                                {getInstagramDisplayHandle({
                                    instagramUsername: acc.instagramUsername,
                                    accountName: acc.pageName,
                                    pageName: acc.pageName,
                                })}
                            </span>
                            <span className="block text-xs text-muted">{acc.pageName}</span>
                        </button>
                    </li>
                ))}
            </ul>
            <Button type="button" variant="ghost" className="mt-4 w-full" onClick={onClose}>
                Cancelar
            </Button>
        </div>
    </div>
);

const InstagramConnections: React.FC = () => {
    const { currentOrganization } = useOrganization();
    const { stripePlans, stripeSubscription } = useSubscription();

    const plan = stripePlans.find((p) => p.id === stripeSubscription?.planId);
    const instagramFeature = plan?.features?.instagramDm ?? 'unavailable';
    const isAvailable = instagramFeature === 'available';
    const isAdmin =
        currentOrganization?.role === 'owner' || currentOrganization?.role === 'admin';

    const {
        connections,
        isLoading,
        isConnecting,
        isSaving,
        isModalOpen,
        accounts,
        connect,
        disconnect,
        save,
        closeModal,
    } = useInstagramConnection(currentOrganization?.id);

    const connected = connections.some((c) => c.connected);
    const active = connections.find((c) => c.connected) ?? connections[0];

    if (!isAvailable) {
        return (
            <Card className="p-4 md:p-6 opacity-90">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <InstagramIcon className="h-9 w-9 sm:h-10 sm:w-10" />
                        <div>
                            <h3 className="text-section-title text-default">Instagram Direct</h3>
                            <p className="text-sm text-muted">
                                {instagramFeature === 'coming_soon'
                                    ? 'Em breve no seu plano.'
                                    : 'Disponível nos planos Pro e Enterprise.'}
                            </p>
                        </div>
                    </div>
                    {instagramFeature !== 'coming_soon' && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                window.location.assign('/faturamento');
                            }}
                        >
                            Fazer upgrade
                        </Button>
                    )}
                </div>
            </Card>
        );
    }

    return (
        <>
            <Card className="p-4 md:p-6">
                <div className="mb-4 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <InstagramIcon className="h-9 w-9 sm:h-10 sm:w-10" />
                        <div>
                            <h3 className="text-section-title text-default">Instagram Direct</h3>
                            <p className="text-xs text-muted">
                                1 conta por organização · Pro / Enterprise
                            </p>
                        </div>
                    </div>
                    {connected ? (
                        <span className="rounded-full bg-success/10 px-3 py-1 text-2xs font-medium text-success">
                            Conectado
                        </span>
                    ) : (
                        <span className="rounded-full bg-surface-subtle px-3 py-1 text-2xs font-medium text-muted">
                            Não conectado
                        </span>
                    )}
                </div>

                {isLoading ? (
                    <p className="text-sm text-muted">Carregando...</p>
                ) : connected && active ? (
                    <div className="space-y-3">
                        <p className="text-sm text-default">
                            <strong>{getInstagramDisplayHandle(active)}</strong>
                        </p>
                        {active.datasetId && (
                            <p className="text-xs text-muted">Dataset: {active.datasetId}</p>
                        )}
                        <p className="text-xs text-muted">
                            Limite: 1 Instagram por organização. Desconecte para trocar de conta.
                        </p>
                        <Button type="button" variant="outline" onClick={() => void disconnect()}>
                            Desconectar
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-sm text-muted">
                            Conecte sua conta Instagram Business para receber mensagens do Direct na inbox.
                        </p>
                        {!isAdmin && (
                            <p className="text-xs text-warning">
                                Apenas administradores podem conectar o Instagram.
                            </p>
                        )}
                        <Button
                            type="button"
                            onClick={() => void connect()}
                            disabled={isConnecting || connected || !isAdmin}
                        >
                            {isConnecting ? 'Conectando...' : 'Conectar Instagram'}
                        </Button>
                    </div>
                )}
            </Card>

            {isModalOpen && accounts.length > 0 && !connected && (
                <AccountPickerModal
                    accounts={accounts}
                    isSaving={isSaving}
                    onSelect={(acc) => void save(acc)}
                    onClose={closeModal}
                />
            )}
        </>
    );
};

export default InstagramConnections;
